/** Kompakte Tagesansicht mit den bestehenden Terminen + dem geplanten Slot.
 *  Zeigt Stunden vom frühesten Event bis zum spätesten (mind. 7-18 Uhr),
 *  geplanter Slot ist farblich hervorgehoben (gruen wenn frei, rot wenn belegt).
 *
 *  Reused von Review (ActionPlanCard) und E-Mail-BookingModal.
 */

export type DayEvent = { start: string; end: string; title: string; partner_ids: number[] }

export function MiniDayCalendar({
  slotStart, slotDurationMin, events, isFree,
}: {
  slotStart: string
  slotDurationMin: number
  events: DayEvent[]
  isFree: boolean
}) {
  const slotDate = slotStart.slice(0, 10)
  const sameDayEvents = events.filter((e) => e.start.slice(0, 10) === slotDate)

  const parseHour = (iso: string): number => {
    const t = iso.slice(11, 16)
    const [h, m] = t.split(":").map(Number)
    return (h || 0) + (m || 0) / 60
  }

  const slotStartH = parseHour(slotStart)
  const slotEndH = slotStartH + slotDurationMin / 60

  const allHours = [
    slotStartH, slotEndH,
    ...sameDayEvents.flatMap((e) => [parseHour(e.start), parseHour(e.end)]),
  ]
  const minH = Math.max(0, Math.floor(Math.min(7, ...allHours) - 0.5))
  const maxH = Math.min(24, Math.ceil(Math.max(18, ...allHours) + 0.5))
  const totalH = maxH - minH

  const dateLabel = new Date(slotDate + "T00:00:00").toLocaleDateString("de-DE", {
    weekday: "short", day: "2-digit", month: "2-digit",
  })

  const slotColor = isFree ? "#16a34a" : "#dc2626"
  const slotBg = isFree ? "rgba(22,163,74,0.15)" : "rgba(220,38,38,0.18)"

  return (
    <div style={{
      marginTop: 8, padding: 10, background: "#fff",
      border: "0.5px solid rgba(44,44,42,0.12)", borderRadius: 6,
    }}>
      <div style={{ fontSize: 12, color: "#5F5E5A", marginBottom: 6, fontWeight: 500 }}>
        Tagesansicht {dateLabel} ({sameDayEvents.length} Termin{sameDayEvents.length !== 1 ? "e" : ""})
      </div>
      <div style={{ position: "relative", height: 60, background: "#FAFAF8", borderRadius: 4, overflow: "hidden" }}>
        {Array.from({ length: totalH + 1 }).map((_, i) => {
          const h = minH + i
          const left = (i / totalH) * 100
          return (
            <div key={i} style={{
              position: "absolute", left: `${left}%`, top: 0, bottom: 0,
              borderLeft: "0.5px solid rgba(44,44,42,0.08)",
              fontSize: 12, color: "#888780", paddingLeft: 2, paddingTop: 2,
            }}>{h}</div>
          )
        })}
        {sameDayEvents.map((e, i) => {
          const eStart = parseHour(e.start)
          const eEnd = parseHour(e.end)
          const left = ((eStart - minH) / totalH) * 100
          const width = ((eEnd - eStart) / totalH) * 100
          if (width <= 0) return null
          return (
            <div key={i}
              title={`${e.title || "Termin"} · ${e.start.slice(11, 16)}–${e.end.slice(11, 16)}`}
              style={{
                position: "absolute", left: `${left}%`, width: `${width}%`,
                top: 18, height: 24,
                background: "rgba(60,52,137,0.25)", border: "0.5px solid rgba(60,52,137,0.4)",
                borderRadius: 3, fontSize: 12, color: "#3C3489",
                padding: "2px 4px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
              }}>
              {e.title || "Termin"}
            </div>
          )
        })}
        <div
          title={`Geplanter Slot · ${slotStart.slice(11, 16)} (${slotDurationMin} Min)`}
          style={{
            position: "absolute",
            left: `${((slotStartH - minH) / totalH) * 100}%`,
            width: `${((slotEndH - slotStartH) / totalH) * 100}%`,
            top: 14, height: 32,
            background: slotBg, border: `1.5px solid ${slotColor}`,
            borderRadius: 4, fontSize: 12, fontWeight: 600, color: slotColor,
            padding: "2px 4px", overflow: "hidden", whiteSpace: "nowrap",
            display: "flex", alignItems: "center",
          }}>
          ★ {slotStart.slice(11, 16)}
        </div>
      </div>
    </div>
  )
}
